// Copyright (c) 2019-2024 Cigent Technology Inc. All Rights Reserved.

import { Component, Input } from '@angular/core';
import { SecurityRequestService } from '../../../../services/security-request.service';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '../../../../services/notification-service';
import { D3eService } from '../../../../services/d3e-service';
import { Store } from '@ngrx/store';
import { AppState } from '../../../store/app.state';
import {
    ISecurityRequestResponse,
    SecurityRequestStatus,
    SecurityRequestType
} from '../../../../types/security-requests';
import { EMPTY, forkJoin, Observable, repeat, Subject, timer } from 'rxjs';
import { IManageUser } from '../../../../types/user';
import { selectUser } from '../../../store/user/user.selectors';
import { OrgType } from '../../../../types/group';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IRestConfig } from '../../../../types/config';
import {
    ISecurityRequestDialogResult,
    SecurityRequestDialogComponent
} from '../../encryption/security-request-dialog/security-request-dialog.component';
import AuthHelper from '../../../auth/authHelper';
import { IVolume } from '../../../../types/subscription';
import {
    SecurityRequestStatusDialogComponent
} from '../../encryption/security-request-status-dialog/security-request-status-dialog.component';
import {
    BitlockerDisplayKeyDialogComponent
} from './bitlocker-display-key-dialog/bitlocker-display-key-dialog.component';
import { AdminSettingsService } from '../../../../services/admin-settings.service';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-bitlocker-key-request',
    templateUrl: './bitlocker-key-request.component.html',
    styleUrls: ['./bitlocker-key-request.component.scss'],
    standalone: false
})
export class BitlockerKeyRequestComponent {
    @Input() endpointName: string;
    @Input() volume: IVolume;
    @Input() restConfig: IRestConfig;

    keyId: string;
    securityRequestsEnabled = false;
    minimumApprovalPercentRequired = 0;
    currentRequest: ISecurityRequestResponse;

    user$: Observable<IManageUser> = this.store.select(selectUser());
    customerType = OrgType.CUSTOMER;

    readonly Status = SecurityRequestStatus;

    requestRefreshObesrvable$: Observable<void>;
    private readonly _stop = new Subject<void>();
    private readonly _start = new Subject<void>();

    constructor(private readonly securityRequestService: SecurityRequestService,
                private readonly dialog: MatDialog,
                private readonly translationService: TranslateService,
                private readonly notificationService: NotificationService,
                private readonly d3eService: D3eService,
                private readonly adminSettingsService: AdminSettingsService,
                private readonly store: Store<AppState>) {
    }

    ngOnInit(): void {
        this.keyId = this.volume.volume_encryption.key_id;
        this.securityRequestsEnabled = this.restConfig.security_requests_enabled;
        this.minimumApprovalPercentRequired = this.restConfig.security_request_min_percent;

        if ( this.keyId && this.keyId.length > 0 ) {
            this.startRequestTimer();
        }
    }

    private startRequestTimer() {
        if ( this.requestRefreshObesrvable$ != null) {
            this._start.next();
        } else {
            this.requestRefreshObesrvable$ = timer(0, 60000)
                .pipe(
                    map(() => {
                        this.getCurrentRequest()
                    }),
                    takeUntil(this._stop),
                    repeat({delay: () => this._start}),
                    untilDestroyed(this)
                );
            this.requestRefreshObesrvable$.subscribe();
        }
    }

    private getCurrentRequest() {
        this.securityRequestService.getCurrentRequest(null, this.keyId, SecurityRequestType.BITLOCKER_KEY).subscribe(currentRequest => {
            if (currentRequest != null) {
                this.setRequest(currentRequest);
            }
        });
    }

    private setRequest(currentRequest: ISecurityRequestResponse) {
        this.currentRequest = currentRequest;
        if ( this.currentRequest == null ||
            this.currentRequest.status === SecurityRequestStatus.DENIED ||
             this.currentRequest.status === SecurityRequestStatus.CANCELLED  ) {
            this._stop.next();
        } else if ( this.currentRequest.status === SecurityRequestStatus.ACCEPTED ) {
            let key_request_access_duration_minutes = 60;
            if ( this.restConfig.key_request_access_duration_minutes ) {
                key_request_access_duration_minutes = this.restConfig.key_request_access_duration_minutes
            }
            const acceptedDated = Date.parse(this.currentRequest.accepted);
            const acceptedWindow = acceptedDated +
                (key_request_access_duration_minutes * 60000);
            const now = new Date().getTime();
            if ( now > acceptedWindow ) {
                this.currentRequest = null;
                this._stop.next();
            }
        }
    }

    requestBitLockerKeyAccess() {
        if ( !this.securityRequestsEnabled ) {
            return;
        }

        forkJoin({
          adminSettings: this.adminSettingsService.getAdminSettings(),
          approvers: this.securityRequestService.getApprovers()
        }).pipe(
            switchMap(({adminSettings, approvers}) => {
                const dialogRef = this.dialog.open(
                    SecurityRequestDialogComponent,
                    {
                        width: '44em',
                        data: {
                            results: approvers.results,
                            minimumApprovalPercentRequired: this.minimumApprovalPercentRequired,
                            securityApprovalMethod: adminSettings.security_approval_method,
                            requestTitle: this.translationService.instant('endpoints.key_request.security_req_title'),
                            subTitle: this.translationService.instant( 'endpoints.key_request.security_request_subtitle',
                                {'volume_name': this.volume.mount + " " + this.volume.label , 'endpoint_name': this.endpointName})
                        },
                    }
                );
                return dialogRef.afterClosed();
            }),
            switchMap((dialogResult: ISecurityRequestDialogResult) => {
                if (dialogResult) {
                    const userId = AuthHelper.getUidFromToken();
                    const otherData = {
                        keyId: this.keyId,
                        endpointName: this.endpointName,
                    };
                    return this.securityRequestService.postSecurityRequest(
                      userId, SecurityRequestType.BITLOCKER_KEY, null, otherData, dialogResult.notifyApprovers);
                }
                return EMPTY;
            }),
            switchMap(() => {
                return this.securityRequestService.getCurrentRequest(null, this.keyId, SecurityRequestType.BITLOCKER_KEY);
            }),
            catchError(error => {
                console.error(error);
                this.notificationService.showError(this.translationService.instant('ERROR_OCCURRED_UNKNOWN'));
                return EMPTY;
            })
        ).subscribe(currentRequest => {
            this.setRequest(currentRequest);
            this.securityRequestService.refresh$.next(null);
            this.notificationService.showSuccess(this.translationService.instant('SUCCESS'));
            this.startRequestTimer();
        });
    }

    viewKeyRequestStatus(requestId: number) {
        this.securityRequestService.getCurrentRequest(null, this.keyId, SecurityRequestType.BITLOCKER_KEY).pipe(
            switchMap(currentRequest => {
                this.setRequest(currentRequest);
                return this.securityRequestService.getApproversStatus(requestId);
            }),
            switchMap(response => {
                const dialogRef = this.dialog.open(
                    SecurityRequestStatusDialogComponent,
                    {
                        width: '44em',
                        data: {
                            results: response.results,
                            minimumApprovalPercentRequired: this.minimumApprovalPercentRequired,
                            requestStatus: this.currentRequest.status,
                            dialogTitle: this.translationService.instant('endpoints.key_request.security_req_title'),
                            subTitle: this.translationService.instant( 'endpoints.key_request.security_request_subtitle',
                                {'volume_name': this.volume.mount + " " + this.volume.label , 'endpoint_name': this.endpointName})
                        }
                    }
                );
                return dialogRef.afterClosed();
            }),
            switchMap((buttonClicked: boolean) => {
                if (buttonClicked) {
                    if (this.currentRequest.status === SecurityRequestStatus.APPROVED) {
                        return this.securityRequestService.putAcceptRequest(this.currentRequest.id);
                    } else if (this.currentRequest.status === SecurityRequestStatus.PENDING) {
                        return this.securityRequestService.putCancelRequest(this.currentRequest.id);
                    }
                }
                return EMPTY;
            }),
            switchMap(() => {
                return this.securityRequestService.getCurrentRequest(null, this.keyId, SecurityRequestType.BITLOCKER_KEY);
            }),
            catchError(() => {
                this.notificationService.showError(this.translationService.instant('ERROR_OCCURRED_UNKNOWN'));
                return EMPTY;
            })
        ).subscribe(currentRequest => {
            this.setRequest(currentRequest);
            this.securityRequestService.refresh$.next(null);
            this.notificationService.showSuccess(this.translationService.instant('SUCCESS'));
        });
    }

    showBitLockerKey(requestId: number) {
        this.d3eService.getVolumeConfig(this.currentRequest.id).subscribe( response => {
            const dialogRef = this.dialog.open(BitlockerDisplayKeyDialogComponent,
                {
                    width: '815px',
                    data: {
                        securityRequest: this.currentRequest,
                        keyId: this.keyId,
                        volume: this.volume,
                        endpointName: this.endpointName,
                        volume_config: response
                    }
                });
        },
        error => {
            this.notificationService.showError(this.translationService.instant('endpoints.key_request.access_failure'));
        })

    }

}
